import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Plus, Trash2, Edit, GripVertical } from "lucide-react";

function StrictModeDroppable(props) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props} />;
}

export default function ServiceCategoryListPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/servicecategory");
      const sorted = response.data
        .map((c, i) => ({
          ...c,
          iconUrl: c.iconPath
            ? `${process.env.REACT_APP_UPLOAD_URL}/${c.iconPath}`
            : null,
          sortOrder: c.sortOrder || i + 1,
        }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCategories(sorted);
    } catch {
      toast.error("Kategoriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDelete = (id) => {
    if (!window.confirm("Bu kategoriyi silmek istediğinize emin misiniz?"))
      return;
    api
      .delete(`/servicecategory/${id}`)
      .then(() => {
        toast.success("Kategori silindi");
        fetchCategories();
      })
      .catch(() => toast.error("Silme işlemi başarısız"));
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const items = Array.from(categories);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setCategories(items);

    try {
      await api.post(
        "/servicecategory/reorder",
        items.map((c, i) => ({ Id: c.serviceCategoryId, OrderNumber: i + 1 })),
      );
      toast.success("Sıralama güncellendi");
    } catch {
      toast.error("Sıralama güncellenemedi");
      fetchCategories();
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Hizmet Kategorileri</h2>
          <p className="page-subtitle">
            {categories.length} kategori listeleniyor
          </p>
        </div>
        <Link to="/add-service-category" className="btn btn-primary">
          <Plus size={18} /> Yeni Kategori
        </Link>
      </div>

      <div className="content-card no-padding">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}></th>
                  <th>İkon</th>
                  <th>Ad (TR)</th>
                  <th>Ad (EN)</th>
                  <th>Renk</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <StrictModeDroppable droppableId="categories">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {categories.map((cat, index) => (
                      <Draggable
                        key={String(cat.serviceCategoryId)}
                        draggableId={String(cat.serviceCategoryId)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? "dragging" : ""}
                          >
                            <td
                              {...provided.dragHandleProps}
                              className="drag-handle"
                            >
                              <GripVertical size={18} />
                            </td>
                            <td>
                              {cat.iconUrl ? (
                                <img
                                  src={cat.iconUrl}
                                  alt=""
                                  className="table-thumb"
                                />
                              ) : (
                                <div className="table-thumb-placeholder" />
                              )}
                            </td>
                            <td className="fw-medium">{cat.nameTr}</td>
                            <td>{cat.nameEn}</td>
                            <td>
                              {cat.colorCode && (
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 24,
                                    height: 24,
                                    borderRadius: 6,
                                    backgroundColor: cat.colorCode,
                                    border: "1px solid #e2e8f0",
                                  }}
                                />
                              )}
                            </td>
                            <td>
                              <span
                                className={`status-badge ${cat.isActive ? "active" : "inactive"}`}
                              >
                                {cat.isActive ? "Aktif" : "Pasif"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/edit-service-category/${cat.serviceCategoryId}`}
                                  className="btn-icon edit"
                                  title="Düzenle"
                                >
                                  <Edit size={16} />
                                </Link>
                                <button
                                  onClick={() =>
                                    handleDelete(cat.serviceCategoryId)
                                  }
                                  className="btn-icon delete"
                                  title="Sil"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </StrictModeDroppable>
            </table>
          </div>
        </DragDropContext>

        {categories.length === 0 && (
          <div className="empty-state">
            <p>Henüz kategori eklenmemiş</p>
            <Link to="/add-service-category" className="btn btn-primary">
              <Plus size={18} /> İlk Kategoriyi Ekle
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
